import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface GlowingEdgesParams {
  edgeWidth: number;
  edgeBrightness: number;
  smoothness: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const GlowingEdges: Filter<GlowingEdgesParams> = {
  id: "glowing-edges",
  displayName: "Glowing Edges",
  defaultParams: { edgeWidth: 2, edgeBrightness: 15, smoothness: 4 },

  apply(input: PixelBuffer, params: GlowingEdgesParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const r = Math.max(1, Math.round(params.edgeWidth));

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const lum = src[o]! * 0.299 + src[o + 1]! * 0.587 + src[o + 2]! * 0.114;

        let maxGrad = 0;
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const sx = Math.max(0, Math.min(w - 1, x + dx));
            const sy = Math.max(0, Math.min(h - 1, y + dy));
            const si = (sy * w + sx) * 4;
            const sLum = src[si]! * 0.299 + src[si + 1]! * 0.587 + src[si + 2]! * 0.114;
            const grad = Math.abs(lum - sLum);
            if (grad > maxGrad) maxGrad = grad;
          }
        }

        const glow = Math.min(255, maxGrad * params.edgeBrightness / 10);
        dst[o] = clamp8(glow);
        dst[o + 1] = clamp8(glow * 0.8);
        dst[o + 2] = clamp8(glow * 0.5);
        dst[o + 3] = 255;
      }
    }
    return out;
  },
};
